import { useState } from 'react';
import PropTypes from 'prop-types';
import sanitizeHtml from 'sanitize-html';
import { useIntl } from 'gatsby-plugin-react-intl';

import {
  Label,
  Blockquote,
  StyledSelect,
  SelectContainer,
  SelectWrapper,
} from './styles';

// * Prefix {string} A prefix for all classes on the pkg select
// * Label {string} The value that displays as a placeholder on the select dropdown
// * Options {Array} An array of objects that create the select options
// * DefaultOptions {Obj} A single option that should be the defaultSelect dropdown option
// * onChange (Func) Called whenever a value changes, and passes back the object of the selected option

const Select = ({
  className = '',
  options,
  defaultOption = null,
  isDisabled = false,
  label = null,
  prefix = 'react-select',
  state = null,
  setState = null,
  displayLabel = 'hide',
  labelTitle = '',
  placeholder = '',
  dataSelectName = '',
}) => {
  if (!options) return null;

  const intl = useIntl();

  const [ariaFocusMessage, setAriaFocusMessage] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleChange = option => {
    setState(option);
  };

  const onFocus = ({ focused }) => {
    const msg = focused.ariaLabel
      ? `${intl.formatMessage({
          id: 'selectFocused',
        })} ${sanitizeHtml(focused.ariaLabel, {
          allowedTags: [],
        })}`
      : `${intl.formatMessage({
          id: 'selectFocused',
        })} ${sanitizeHtml(focused.label, {
          allowedTags: [],
        })}`;
    setAriaFocusMessage(msg);
    return msg;
  };

  const onMenuOpen = () => setIsMenuOpen(true);
  const onMenuClose = () => setIsMenuOpen(false);

  return (
    <SelectContainer className={className}>
      <Label id="aria-label" className={displayLabel} htmlFor="aria-input">
        {labelTitle ||
          `${intl.formatMessage({
            id: 'select',
          })} ${intl.formatMessage({
            id: label,
          })}`}
      </Label>
      <SelectWrapper>
        {!!ariaFocusMessage && !!isMenuOpen && (
          <Blockquote>{ariaFocusMessage}</Blockquote>
        )}
        <StyledSelect
          aria-labelledby="aria-label"
          ariaLiveMessages={{
            onFocus,
          }}
          id={dataSelectName}
          className={`${className} ${dataSelectName}`}
          classNamePrefix={prefix}
          defaultOption={defaultOption}
          inputId="aria-input"
          isClearable={false}
          isDisabled={isDisabled}
          isSearchable={false}
          options={options}
          onChange={handleChange}
          onMenuClose={onMenuClose}
          onMenuOpen={onMenuOpen}
          value={state}
          placeholder={placeholder}
        />
      </SelectWrapper>
    </SelectContainer>
  );
};

Select.propTypes = {
  className: PropTypes.string,
  defaultOption: PropTypes.shape(),
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape).isRequired,
  prefix: PropTypes.string,
  setState: PropTypes.func,
  state: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.shape()),
    PropTypes.bool,
  ]),
  displayLabel: PropTypes.string,
  labelTitle: PropTypes.string,
  placeholder: PropTypes.string,
  dataSelectName: PropTypes.string,
};

export default Select;
